import {
  Button,
  Checkbox,
  Divider,
  Flex,
  Select,
  Space,
  Tooltip,
  Typography
} from 'antd'
import { Info } from 'lucide-react'
import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { OrganizationRoleFull, PermissionsLocalized } from 'src/api/main'

import { areAllPermissionsSelected, getPermissionLabel } from './utils'

const { Text } = Typography

type Props = {
  role: OrganizationRoleFull
  permissions: PermissionsLocalized[]
  value: number[]
  onChange: (value: number[]) => void
}

const isViewCode = (code?: string) => Boolean(code?.startsWith('view_'))

const arraysEqual = (prev: number[] = [], next: number[] = []) =>
  prev.length === next.length && prev.every((v, i) => v === next[i])

const permsEqual = (
  prev: PermissionsLocalized[] = [],
  next: PermissionsLocalized[] = []
) =>
  prev.length === next.length &&
  prev.every((p, i) => p.id === next[i]?.id && p.code === next[i]?.code)

type PermissionOption = {
  value: number
  label: string
  code: string
  hasAllSuffix: boolean
  disabled: boolean
}

export const RolePermissionCell = memo(
  ({ role, permissions, value, onChange }: Props) => {
    const { t } = useTranslation()
    const isAdmin = role.is_admin
    // Form.Item injects `value` after the first render — guard so the cell
    // can render its empty state without crashing on `.length` / Set ctor.
    const safeValue = value ?? []

    const valueSet = useMemo(() => new Set(safeValue), [safeValue])

    const { allIds, hasViewPermission, hasSelectedView } = useMemo(() => {
      const ids: number[] = []
      let hasView = false
      let viewSelected = false
      for (const p of permissions) {
        ids.push(p.id)
        if (!isViewCode(p.code)) continue
        hasView = true
        if (valueSet.has(p.id)) viewSelected = true
      }

      return {
        allIds: ids,
        hasViewPermission: hasView,
        hasSelectedView: viewSelected
      }
    }, [permissions, valueSet])

    const allSelected = useMemo(
      () => areAllPermissionsSelected(safeValue, allIds),
      [safeValue, allIds]
    )

    const options: PermissionOption[] = useMemo(
      () =>
        permissions.map((p) => {
          const { labelText, hasAllSuffix } = getPermissionLabel(p.code)
          const isView = isViewCode(p.code)
          // Non-view permissions are unselectable until a view permission is
          // active — mirrors the previous checkbox-grid behaviour.
          const lockedByView = !isView && hasViewPermission && !hasSelectedView

          return {
            value: p.id,
            label: labelText,
            code: p.code,
            hasAllSuffix,
            disabled: lockedByView
          }
        }),
      [permissions, hasViewPermission, hasSelectedView]
    )

    const handleChange = (next: number[]) => {
      if (isAdmin) return

      // If the user just removed the last `view_*` permission, clear the
      // whole cell — non-view permissions can't survive without a view.
      const droppedIds = safeValue.filter((id) => !next.includes(id))
      if (droppedIds.length > 0) {
        const droppedAView = droppedIds.some((id) => {
          const target = permissions.find((p) => p.id === id)

          return target ? isViewCode(target.code) : false
        })
        if (droppedAView) {
          const stillHasView = next.some((id) => {
            const target = permissions.find((p) => p.id === id)

            return target ? isViewCode(target.code) : false
          })
          if (!stillHasView) {
            onChange([])

            return
          }
        }
      }
      onChange(next)
    }

    const toggleSelectAll = (nextChecked: boolean) => {
      if (isAdmin) return
      onChange(nextChecked ? allIds : [])
    }

    const placeholder = isAdmin
      ? t('workspace.apps.roles.adminAll')
      : t('workspace.apps.roles.noPermissions')

    return (
      <Select<number[]>
        mode="multiple"
        value={isAdmin ? allIds : safeValue}
        onChange={handleChange}
        disabled={isAdmin}
        options={options}
        showSearch={{ optionFilterProp: 'label' }}
        maxTagCount="responsive"
        placeholder={placeholder}
        style={{ width: '100%', minWidth: 220 }}
        size="small"
        optionRender={(option) => {
          const opt = option.data as PermissionOption
          const checked = isAdmin || valueSet.has(opt.value)
          const row = (
            <Flex align="center" gap={8}>
              <Checkbox checked={checked} disabled={opt.disabled} />
              <Text
                style={{ fontSize: 13 }}
                type={opt.disabled ? 'secondary' : undefined}
              >
                {opt.label}
              </Text>
              {opt.hasAllSuffix ? (
                <Tooltip title={t('workspace.apps.roles.allPermissionTooltip')}>
                  <Info size={12} color="var(--ant-color-text-tertiary)" />
                </Tooltip>
              ) : null}
            </Flex>
          )

          return opt.disabled ? (
            <Tooltip
              title={t('workspace.apps.roles.selectViewFirst')}
              placement="left"
            >
              {row}
            </Tooltip>
          ) : (
            row
          )
        }}
        popupRender={(menu) => (
          <>
            <div
              style={{
                padding: '6px 10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8
              }}
            >
              <Checkbox
                checked={allSelected || isAdmin}
                indeterminate={!isAdmin && safeValue.length > 0 && !allSelected}
                disabled={isAdmin}
                onChange={(e) => toggleSelectAll(e.target.checked)}
              >
                <strong>{t('workspace.apps.roles.selectAll')}</strong>
              </Checkbox>
              <Space
                size={0}
                separator={
                  <Divider orientation="vertical" style={{ margin: 0 }} />
                }
              >
                <Button
                  type="link"
                  size="small"
                  style={{ paddingInline: 0, height: 'auto' }}
                  disabled={isAdmin || safeValue.length === 0}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    toggleSelectAll(false)
                  }}
                >
                  {t('workspace.apps.roles.deselectAll')}
                </Button>
              </Space>
            </div>
            <Divider style={{ margin: 0 }} />
            {menu}
          </>
        )}
      />
    )
  },
  (prev, next) =>
    prev.role.id === next.role.id &&
    prev.role.is_admin === next.role.is_admin &&
    arraysEqual(prev.value, next.value) &&
    permsEqual(prev.permissions, next.permissions)
)

RolePermissionCell.displayName = 'RolePermissionCell'
